import { useEffect, useRef } from "react";
import { useChatStore } from "../../stores/chatStore";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";

// Trash icon (outline style)
function TrashIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

// Chat icon (outline style)
function ChatIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  );
}

// Loading dots animation
function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-gray-100 rounded-lg px-4 py-2">
        <div className="flex gap-1">
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
        </div>
      </div>
    </div>
  );
}

interface ChatPanelProps {
  activityId: string;
  provider?: string;
}

export function ChatPanel({
  activityId,
  provider = "garmin",
}: ChatPanelProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { sessions, sendMessage, clearChat, openChat } = useChatStore();

  const session = sessions[activityId];
  const messages = session?.messages || [];
  const isLoading = session?.isLoading || false;
  const error = session?.error || null;

  // Load chat history on mount
  useEffect(() => {
    openChat(activityId, provider);
  }, [activityId, provider, openChat]);

  // Auto-scroll to bottom when messages change (block: "nearest" prevents page scroll)
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, isLoading]);

  const handleSend = (content: string) => {
    sendMessage(activityId, content, provider);
  };

  const handleClear = () => {
    clearChat(activityId);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 flex flex-col h-[600px] md:h-auto md:max-h-[1000px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <ChatIcon className="w-5 h-5 text-brand-600" />
          <h3 className="font-medium text-gray-900">Ask Questions</h3>
        </div>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            disabled={isLoading}
            className="p-1 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
            title="Clear chat"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
            <ChatIcon className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-sm">
              Ask follow-up questions about your run analysis
            </p>
            <p className="text-xs mt-1 text-gray-400">
              E.g., "What should I focus on for my next run?"
            </p>
          </div>
        )}

        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {isLoading && <TypingIndicator />}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="p-4 border-t border-gray-200">
        <ChatInput onSend={handleSend} disabled={isLoading} />
      </div>
    </div>
  );
}
